import { cn } from '@/lib/utils';
import { type ButtonHTMLAttributes, forwardRef } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, children, disabled, ...props }, ref) => {
    const base =
      'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
      primary:
        'bg-brand-500 text-white hover:bg-brand-600 focus:ring-brand-500',
      secondary:
        'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 focus:ring-gray-300',
      ghost:
        'text-gray-600 hover:bg-gray-100 focus:ring-gray-300',
      danger:
        'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    };

    // `min-h-11` (44px) on TOUCH widths only — `sm` renders ~32px tall, below the
    // 44px minimum, and it is the dominant variant across the admin pages that
    // agents now use on a phone. `md:min-h-0` hands the compact height straight
    // back on desktop, so no desktop layout shifts.
    const sizes = {
      sm: 'px-3 py-1.5 text-sm gap-1.5 min-h-11 md:min-h-0',
      md: 'px-4 py-2 text-sm gap-2 min-h-11 md:min-h-0',
      lg: 'px-6 py-3 text-base gap-2',
    };

    return (
      <button data-btn={variant}
        ref={ref}
        className={cn(base, variants[variant], sizes[size], className)}
        disabled={disabled ?? loading}
        {...props}
      >
        {loading && (
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
